import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import backIcon from '@iconify/icons-ant-design/arrow-left-outline';
import uploadFill from '@iconify/icons-eva/cloud-upload-fill';

// material
import {
  Stack,
  TextField,
  Button,
  Card,
  CardHeader,
  CardContent,
  Container,
  Typography,
  Box,
  IconButton,
  Grid,
  Avatar,
  MenuItem
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

// components
import Page from '../components/Page';
import api from '../api';
import config from '../config.json';

export default function LeagueForm() {
  const navigate = useNavigate();
  const { league_id } = useParams();
  const isEdit = Boolean(league_id);
  const [imagePreview, setImagePreview] = useState(null);

  const LeagueSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    author: Yup.string().required('Author is required'),
    type: Yup.string().required('Type is required'),
    begin_date: Yup.date().required('Begin date is required'),
    end_date: Yup.date()
      .required('End date is required')
      .min(Yup.ref('begin_date'), 'End date must be after begin date'),
    image: Yup.mixed()
      .test('fileSize', 'Image size must be less than 2MB', (value) => {
        if (!value || !(value instanceof File)) return true;
        return value.size <= 2 * 1024 * 1024; // 2MB in bytes
      })
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      author: '',
      type: 'public',
      begin_date: '',
      end_date: '',
      image: null
    },
    validationSchema: LeagueSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('author', values.author);
      formData.append('type', values.type);
      formData.append('begin_date', values.begin_date);
      formData.append('end_date', values.end_date);
      if (values.image instanceof File) {
        formData.append('image', values.image);
      }

      try {
        const url = isEdit ? `/leagues/edit/${league_id}` : '/leagues/create';
        // Agar edit bo'lsa PUT yoki POST (API sturkturasiga qarab, odatda FormData bilan POST ishlatiladi va _method=PUT qo'shilishi mumkin yoki backend to'g'ridan-to'g'ri POST qabul qiladi update uchun)
        // Bu yerda men umumiy holda POST ishlataman, agar RESTful bo'lsa methodni o'zgartirish kerak bo'lishi mumkin.
        const method = isEdit ? "POST" : "POST"; 
        
        // Agar PUT kerak bo'lsa va backend FormData bilan PUT ni to'g'ri o'qisa, methodni o'zgartiring.
        // Ko'p hollarda fayl yuklashda POST ishlatib, update ekanligini URL orqali bildirishadi.
        
        const response = await api.query(url, method, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': api.getToken()
          }
        });

        if (response.success) {
          navigate('/dashboard/leagues', { state: { reload: true } });
        } else {
          alert(response.message || 'Error occurred');
        }
      } catch (error) {
        console.error(error);
        alert('Something went wrong');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, setFieldValue } = formik;

  useEffect(() => {
    if (isEdit) {
      loadLeagueData();
    }
  }, [isEdit]);

  const loadLeagueData = () => {
    api.get(`/leagues/${league_id}`).then((response) => {
      if (response.success) {
        const data = response.data;
        setFieldValue('name', data.name);
        setFieldValue('author', data.author);
        setFieldValue('type', data.type || 'public');
        // Convert dd.mm.yyyy HH:mm to yyyy-mm-dd
        const beginDate = data.begin_date.split(' ')[0].split('.').reverse().join('-');
        const endDate = data.end_date.split(' ')[0].split('.').reverse().join('-');
        setFieldValue('begin_date', beginDate);
        setFieldValue('end_date', endDate);
        if (data.image) {
           setImagePreview(`https://files.eagles.uz/leagues/${data.image}`);
        }
      }
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Image size must be less than 2MB');
        return;
      }
      setFieldValue('image', file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  return (
    <Page title={isEdit ? "Edit League | Eagles.uz" : "Create League | Eagles.uz"}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Stack direction="row" alignItems="center" spacing={2}>
                 <IconButton onClick={() => navigate(-1)} color="success">
                    <Icon icon={backIcon} width={24} height={24} />
                 </IconButton>
                 <Typography variant="h4" gutterBottom>
                    {isEdit ? 'Edit League' : 'Create New League'}
                 </Typography>
            </Stack>
        </Stack>

        <Card>
          <CardHeader title="League Details" subheader="Fill in the information below" />
          <CardContent>
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="League Name"
                        {...getFieldProps('name')}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />

                      <TextField
                        fullWidth
                        label="Author"
                        {...getFieldProps('author')}
                        error={Boolean(touched.author && errors.author)}
                        helperText={touched.author && errors.author}
                      />

                      <TextField
                        select
                        fullWidth
                        label="Type"
                        {...getFieldProps('type')}
                        error={Boolean(touched.type && errors.type)}
                        helperText={touched.type && errors.type}
                      >
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                      </TextField>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="Begin Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          {...getFieldProps('begin_date')}
                          error={Boolean(touched.begin_date && errors.begin_date)}
                          helperText={touched.begin_date && errors.begin_date}
                        />

                        <TextField
                          fullWidth
                          label="End Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          {...getFieldProps('end_date')}
                          error={Boolean(touched.end_date && errors.end_date)}
                          helperText={touched.end_date && errors.end_date}
                        />
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>
                     <Card sx={{ p: 3, textAlign: 'center', border: '1px dashed grey' }}>
                        <Typography variant="subtitle2" gutterBottom>
                            League Image
                        </Typography>
                        
                        <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                            {imagePreview ? (
                                <Avatar 
                                    src={imagePreview} 
                                    alt="League"
                                    sx={{ width: 120, height: 120 }}
                                    variant="rounded"
                                />
                            ) : (
                                <Box sx={{ width: 120, height: 120, bgcolor: 'background.neutral', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon icon="eva:image-outline" width={48} height={48} color="text.disabled" />
                                </Box>
                            )}
                        </Box>

                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="raised-button-file"
                            type="file"
                            onChange={handleImageChange}
                        />
                        <label htmlFor="raised-button-file">
                            <Button variant="outlined" component="span" startIcon={<Icon icon={uploadFill} />}>
                                Upload Image
                            </Button>
                        </label>
                        {touched.image && errors.image && (
                           <Typography variant="caption" color="error" sx={{ display:'block', mt:1 }}>
                             {errors.image}
                           </Typography>
                        )}
                     </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={isSubmitting}
                    >
                      {isEdit ? 'Save Changes' : 'Create League'}
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
}